import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Moon, ArrowLeft, User, Briefcase, DollarSign, FileText,
  Clock, Plus, Trash2, Edit2, Save, X, CheckCircle,
  AlertCircle, Package, Plane, Hotel, Bus, Globe
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  confirmed:  "bg-green-100 text-green-800 border-green-200",
  pending:    "bg-yellow-100 text-yellow-800 border-yellow-200",
  cancelled:  "bg-red-100 text-red-700 border-red-200",
  completed:  "bg-blue-100 text-blue-800 border-blue-200",
};

const VISA_STATUS_COLORS = {
  pending:   "bg-yellow-100 text-yellow-800",
  submitted: "bg-blue-100 text-blue-800",
  approved:  "bg-green-100 text-green-800",
  rejected:  "bg-red-100 text-red-700",
};

const SERVICE_ICONS = {
  visa:      <Globe size={14} />,
  flight:    <Plane size={14} />,
  hotel:     <Hotel size={14} />,
  transport: <Bus size={14} />,
  other:     <Package size={14} />,
};

const SERVICE_COLORS = {
  visa:      "bg-green-50 text-green-700 border-green-200",
  flight:    "bg-blue-50 text-blue-700 border-blue-200",
  hotel:     "bg-purple-50 text-purple-700 border-purple-200",
  transport: "bg-amber-50 text-amber-700 border-amber-200",
  other:     "bg-gray-50 text-gray-700 border-gray-200",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

const TABS = [
  { key: "overview",   label: "Overview",   icon: <Moon size={14} /> },
  { key: "pilgrims",   label: "Pilgrims",   icon: <User size={14} /> },
  { key: "services",   label: "Services",   icon: <Briefcase size={14} /> },
  { key: "financials", label: "Financials", icon: <DollarSign size={14} /> },
  { key: "documents",  label: "Documents",  icon: <FileText size={14} /> },
  { key: "activity",   label: "Activity",   icon: <Clock size={14} /> },
];

// ── Pilgrim Form ──────────────────────────────────────────────────────────────
const BLANK_PILGRIM = {
  title: "", first_name: "", last_name: "", date_of_birth: "", nationality: "",
  gender: "", passport_number: "", passport_expiry: "", passport_issue_country: "",
  room_type: "", room_number: "", is_lead: false,
  visa_status: "pending", visa_reference: "", document_status: "incomplete", notes: "",
};

function PilgrimForm({ initial = BLANK_PILGRIM, onSave, onCancel, saving }) {
  const [f, setF] = useState({ ...BLANK_PILGRIM, ...initial });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Title</label>
          <select value={f.title} onChange={e => set("title", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">—</option>
            {["Mr", "Mrs", "Miss", "Dr", "Sheikh"].map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">First Name *</label>
          <input value={f.first_name} onChange={e => set("first_name", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="First name" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Last Name *</label>
          <input value={f.last_name} onChange={e => set("last_name", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="Last name" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Gender</label>
          <select value={f.gender} onChange={e => set("gender", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">—</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Date of Birth</label>
          <input type="date" value={f.date_of_birth} onChange={e => set("date_of_birth", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Nationality</label>
          <input value={f.nationality} onChange={e => set("nationality", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="e.g. British" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Passport #</label>
          <input value={f.passport_number} onChange={e => set("passport_number", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono" placeholder="Passport number" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Passport Expiry</label>
          <input type="date" value={f.passport_expiry} onChange={e => set("passport_expiry", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Issue Country</label>
          <input value={f.passport_issue_country} onChange={e => set("passport_issue_country", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="e.g. United Kingdom" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Room Type</label>
          <select value={f.room_type} onChange={e => set("room_type", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">—</option>
            {["single", "double", "triple", "quad"].map(t => <option key={t} className="capitalize">{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Room #</label>
          <input value={f.room_number} onChange={e => set("room_number", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="Room number" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Visa Status</label>
          <select value={f.visa_status} onChange={e => set("visa_status", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
            {["pending", "submitted", "approved", "rejected"].map(s => <option key={s} className="capitalize">{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Visa Ref</label>
          <input value={f.visa_reference} onChange={e => set("visa_reference", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="Visa reference" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Docs</label>
          <select value={f.document_status} onChange={e => set("document_status", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="incomplete">Incomplete</option>
            <option value="complete">Complete</option>
          </select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={f.is_lead} onChange={e => set("is_lead", e.target.checked)} className="rounded" />
            <span className="text-sm text-gray-700 font-medium">Lead traveller</span>
          </label>
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Notes</label>
        <textarea value={f.notes} onChange={e => set("notes", e.target.value)} rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="Any notes…" />
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
        <button
          disabled={saving || !f.first_name || !f.last_name}
          onClick={() => onSave(f)}
          className="px-4 py-2 text-sm font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save Pilgrim"}
        </button>
      </div>
    </div>
  );
}

// ── Service Form ──────────────────────────────────────────────────────────────
const BLANK_SERVICE = { service_type: "visa", description: "", cost: "", currency: "EUR", status: "pending", notes: "" };

function ServiceForm({ initial = BLANK_SERVICE, onSave, onCancel, saving }) {
  const [f, setF] = useState({ ...BLANK_SERVICE, ...initial, cost: initial.cost ?? "" });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Type *</label>
          <select value={f.service_type} onChange={e => set("service_type", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
            {["visa", "flight", "hotel", "transport", "other"].map(t => <option key={t} className="capitalize">{t}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Description *</label>
          <input value={f.description} onChange={e => set("description", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="Brief description" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Cost</label>
          <input type="number" value={f.cost} onChange={e => set("cost", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="0.00" min="0" step="0.01" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Currency</label>
          <select value={f.currency} onChange={e => set("currency", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
            {["EUR", "GBP", "USD", "SAR"].map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Status</label>
          <select value={f.status} onChange={e => set("status", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
            {["pending", "confirmed", "cancelled"].map(s => <option key={s} className="capitalize">{s}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Notes</label>
        <textarea value={f.notes} onChange={e => set("notes", e.target.value)} rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="Any notes…" />
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
        <button
          disabled={saving || !f.description}
          onClick={() => onSave({ ...f, cost: f.cost === "" ? null : parseFloat(f.cost) })}
          className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save Service"}
        </button>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function UmrahBookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [msg, setMsg] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Pilgrim state
  const [showAddPilgrim, setShowAddPilgrim] = useState(false);
  const [editPilgrim, setEditPilgrim] = useState(null);

  // Service state
  const [showAddService, setShowAddService] = useState(false);
  const [editService, setEditService] = useState(null);

  // Overview edit
  const [editingOverview, setEditingOverview] = useState(false);
  const [overviewForm, setOverviewForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/umrah-bookings/${id}`, { headers: getAuthHeaders() });
      setBooking(r.data);
      setOverviewForm({
        status: r.data.status,
        customer_name: r.data.customer_name || "",
        customer_email: r.data.customer_email || "",
        customer_phone: r.data.customer_phone || "",
        travel_start_date: r.data.travel_start_date || "",
        travel_end_date: r.data.travel_end_date || "",
        flexible_dates: r.data.flexible_dates || false,
        number_of_travellers: r.data.number_of_travellers || 1,
        internal_notes: r.data.internal_notes || "",
      });
    } catch { setMsg({ text: "Failed to load booking.", type: "error" }); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const showMsg = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  };

  // ── Overview save ──
  const saveOverview = async () => {
    setActionLoading("overview");
    try {
      const r = await axios.patch(`${API}/umrah-bookings/${id}`, overviewForm, { headers: getAuthHeaders() });
      setBooking(r.data);
      setEditingOverview(false);
      showMsg("Booking updated.");
    } catch { showMsg("Failed to update booking.", "error"); }
    finally { setActionLoading(null); }
  };

  // ── Pilgrim actions ──
  const addPilgrim = async (data) => {
    setActionLoading("add-pilgrim");
    try {
      const r = await axios.post(`${API}/umrah-bookings/${id}/pilgrims`, data, { headers: getAuthHeaders() });
      setBooking(r.data);
      setShowAddPilgrim(false);
      showMsg("Pilgrim added.");
    } catch { showMsg("Failed to add pilgrim.", "error"); }
    finally { setActionLoading(null); }
  };

  const updatePilgrim = async (pilgrimId, data) => {
    setActionLoading(`edit-pilgrim-${pilgrimId}`);
    try {
      const r = await axios.patch(`${API}/umrah-bookings/${id}/pilgrims/${pilgrimId}`, data, { headers: getAuthHeaders() });
      setBooking(r.data);
      setEditPilgrim(null);
      showMsg("Pilgrim updated.");
    } catch { showMsg("Failed to update pilgrim.", "error"); }
    finally { setActionLoading(null); }
  };

  const deletePilgrim = async (pilgrimId) => {
    if (!window.confirm("Remove this pilgrim?")) return;
    setActionLoading(`del-pilgrim-${pilgrimId}`);
    try {
      const r = await axios.delete(`${API}/umrah-bookings/${id}/pilgrims/${pilgrimId}`, { headers: getAuthHeaders() });
      setBooking(r.data);
      showMsg("Pilgrim removed.");
    } catch { showMsg("Failed to remove pilgrim.", "error"); }
    finally { setActionLoading(null); }
  };

  // ── Service actions ──
  const addService = async (data) => {
    setActionLoading("add-service");
    try {
      const r = await axios.post(`${API}/umrah-bookings/${id}/services`, data, { headers: getAuthHeaders() });
      setBooking(r.data);
      setShowAddService(false);
      showMsg("Service added.");
    } catch { showMsg("Failed to add service.", "error"); }
    finally { setActionLoading(null); }
  };

  const updateService = async (serviceId, data) => {
    setActionLoading(`edit-service-${serviceId}`);
    try {
      const r = await axios.patch(`${API}/umrah-bookings/${id}/services/${serviceId}`, data, { headers: getAuthHeaders() });
      setBooking(r.data);
      setEditService(null);
      showMsg("Service updated.");
    } catch { showMsg("Failed to update service.", "error"); }
    finally { setActionLoading(null); }
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm("Remove this service?")) return;
    setActionLoading(`del-service-${serviceId}`);
    try {
      const r = await axios.delete(`${API}/umrah-bookings/${id}/services/${serviceId}`, { headers: getAuthHeaders() });
      setBooking(r.data);
      showMsg("Service removed.");
    } catch { showMsg("Failed to remove service.", "error"); }
    finally { setActionLoading(null); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!booking) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center text-gray-500">Booking not found.</div>
      </DashboardLayout>
    );
  }

  const snap = booking.snapshot || {};
  const pilgrims = booking.pilgrims || [];
  const services = booking.services || [];
  const totalServiceCost = services.reduce((s, svc) => s + (svc.cost || 0), 0);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto space-y-5">

        {msg && (
          <div className={`rounded-lg px-4 py-3 text-sm font-semibold ${msg.type === "error" ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
            {msg.text}
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-4">
          <button onClick={() => navigate("/umrah-bookings")} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg mt-1">
            <ArrowLeft size={16} />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Moon className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl font-bold text-gray-900 font-mono">{booking.booking_reference}</h1>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${STATUS_COLORS[booking.status] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
                    {booking.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{booking.customer_name}</p>
              </div>
            </div>
          </div>
          {booking.quotation_number && (
            <div className="text-right">
              <div className="text-xs text-gray-400">From quotation</div>
              <div className="text-sm font-mono text-amber-700 font-semibold">{booking.quotation_number}</div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex gap-1 overflow-x-auto">
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  tab === t.key
                    ? "border-amber-600 text-amber-700"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
              >
                {t.icon}{t.label}
                {t.key === "pilgrims" && pilgrims.length > 0 && (
                  <span className="ml-1 bg-amber-100 text-amber-800 text-xs px-1.5 py-0.5 rounded-full">{pilgrims.length}</span>
                )}
                {t.key === "services" && services.length > 0 && (
                  <span className="ml-1 bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded-full">{services.length}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── OVERVIEW TAB ── */}
        {tab === "overview" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Customer & Booking Info */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Booking Details</h2>
                {!editingOverview
                  ? <button onClick={() => setEditingOverview(true)} className="p-1.5 text-gray-400 hover:text-amber-700 rounded-lg hover:bg-amber-50"><Edit2 size={14} /></button>
                  : <div className="flex gap-1">
                      <button onClick={() => setEditingOverview(false)} className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg"><X size={14} /></button>
                      <button disabled={actionLoading === "overview"} onClick={saveOverview} className="p-1.5 text-green-600 hover:text-green-800 rounded-lg hover:bg-green-50 disabled:opacity-50"><Save size={14} /></button>
                    </div>
                }
              </div>

              {editingOverview ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 block mb-1">Status</label>
                      <select value={overviewForm.status} onChange={e => setOverviewForm(p => ({ ...p, status: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                        {["confirmed", "pending", "completed", "cancelled"].map(s => <option key={s} className="capitalize">{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 block mb-1">Travellers</label>
                      <input type="number" value={overviewForm.number_of_travellers} onChange={e => setOverviewForm(p => ({ ...p, number_of_travellers: parseInt(e.target.value) || 1 }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" min="1" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Customer Name</label>
                    <input value={overviewForm.customer_name} onChange={e => setOverviewForm(p => ({ ...p, customer_name: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Email</label>
                    <input type="email" value={overviewForm.customer_email} onChange={e => setOverviewForm(p => ({ ...p, customer_email: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Phone</label>
                    <input value={overviewForm.customer_phone} onChange={e => setOverviewForm(p => ({ ...p, customer_phone: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 block mb-1">Travel Start</label>
                      <input type="date" value={overviewForm.travel_start_date} onChange={e => setOverviewForm(p => ({ ...p, travel_start_date: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 block mb-1">Travel End</label>
                      <input type="date" value={overviewForm.travel_end_date} onChange={e => setOverviewForm(p => ({ ...p, travel_end_date: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Internal Notes</label>
                    <textarea value={overviewForm.internal_notes} onChange={e => setOverviewForm(p => ({ ...p, internal_notes: e.target.value }))} rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </div>
              ) : (
                <dl className="space-y-2">
                  {[
                    ["Customer", booking.customer_name],
                    ["Email", booking.customer_email],
                    ["Phone", booking.customer_phone],
                    ["Travellers", booking.number_of_travellers],
                    ["Travel Dates", booking.flexible_dates ? "Flexible" : (booking.travel_start_date ? `${fmtDate(booking.travel_start_date)} – ${fmtDate(booking.travel_end_date)}` : "—")],
                    ["Package Ref", booking.package_reference],
                    ["Created", fmtDateTime(booking.created_at)],
                  ].map(([label, val]) => val ? (
                    <div key={label} className="flex justify-between gap-4">
                      <dt className="text-xs text-gray-500 shrink-0">{label}</dt>
                      <dd className="text-sm text-gray-800 text-right">{val}</dd>
                    </div>
                  ) : null)}
                  {booking.internal_notes && (
                    <div className="pt-2 border-t border-gray-100">
                      <dt className="text-xs text-gray-500 mb-1">Internal Notes</dt>
                      <dd className="text-sm text-gray-700 bg-yellow-50 rounded-lg p-2">{booking.internal_notes}</dd>
                    </div>
                  )}
                </dl>
              )}
            </div>

            {/* Package Snapshot */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Package at Booking Time</h2>
              {snap.package_name ? (
                <dl className="space-y-2">
                  {[
                    ["Package", snap.package_name],
                    ["Occupancy", booking.selected_occupancy],
                    ["Makkah Hotel", snap.makkah_accommodation?.hotel_name],
                    ["Madinah Hotel", snap.madinah_accommodation?.hotel_name],
                    ["Nights (Total)", snap.total_nights ? `${snap.total_nights} nights` : null],
                    ["Visa Included", snap.visa_included ? "Yes" : "No"],
                  ].map(([label, val]) => val ? (
                    <div key={label} className="flex justify-between gap-4">
                      <dt className="text-xs text-gray-500 shrink-0">{label}</dt>
                      <dd className="text-sm text-gray-800 text-right">{val}</dd>
                    </div>
                  ) : null)}
                </dl>
              ) : (
                <p className="text-sm text-gray-400 italic">No package snapshot.</p>
              )}

              {/* Financial summary */}
              <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">Agreed Pricing (locked)</h3>
                <div className="flex justify-between">
                  <span className="text-xs text-gray-500">Per person</span>
                  <span className="text-sm font-semibold text-gray-800">
                    {booking.agreed_currency} {(booking.agreed_price_per_person || 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}
                  </span>
                </div>
                <div className="flex justify-between border-t border-gray-100 pt-2">
                  <span className="text-sm font-bold text-gray-700">Total</span>
                  <span className="text-lg font-bold text-amber-800">
                    {booking.agreed_currency} {(booking.agreed_total_price || 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── PILGRIMS TAB ── */}
        {tab === "pilgrims" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">
                Pilgrims ({pilgrims.length} / {booking.number_of_travellers})
              </h2>
              {!showAddPilgrim && (
                <button
                  onClick={() => { setShowAddPilgrim(true); setEditPilgrim(null); }}
                  className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700"
                >
                  <Plus size={14} /> Add Pilgrim
                </button>
              )}
            </div>

            {showAddPilgrim && (
              <PilgrimForm
                onSave={addPilgrim}
                onCancel={() => setShowAddPilgrim(false)}
                saving={actionLoading === "add-pilgrim"}
              />
            )}

            {pilgrims.length === 0 && !showAddPilgrim ? (
              <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-sm">
                No pilgrims added yet. Click "Add Pilgrim" to get started.
              </div>
            ) : (
              <div className="space-y-3">
                {pilgrims.map((p, idx) => (
                  <div key={p.id}>
                    {editPilgrim === p.id ? (
                      <PilgrimForm
                        initial={p}
                        onSave={data => updatePilgrim(p.id, data)}
                        onCancel={() => setEditPilgrim(null)}
                        saving={actionLoading === `edit-pilgrim-${p.id}`}
                      />
                    ) : (
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-sm font-bold text-amber-800">
                              {idx + 1}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900">
                                {p.title && `${p.title} `}{p.first_name} {p.last_name}
                                {p.is_lead && <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full font-semibold">Lead</span>}
                              </div>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {p.nationality && <span className="text-xs text-gray-500">{p.nationality}</span>}
                                {p.gender && <span className="text-xs text-gray-400 capitalize">• {p.gender}</span>}
                                {p.date_of_birth && <span className="text-xs text-gray-400">• DOB: {fmtDate(p.date_of_birth)}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button onClick={() => { setEditPilgrim(p.id); setShowAddPilgrim(false); }} className="p-1.5 text-gray-400 hover:text-amber-700 hover:bg-amber-50 rounded-md">
                              <Edit2 size={13} />
                            </button>
                            <button disabled={!!actionLoading} onClick={() => deletePilgrim(p.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md disabled:opacity-40">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                          <div>
                            <span className="text-gray-400">Passport</span>
                            <div className="font-mono text-gray-700">{p.passport_number || "—"}</div>
                          </div>
                          <div>
                            <span className="text-gray-400">Passport Expiry</span>
                            <div className="text-gray-700">{fmtDate(p.passport_expiry)}</div>
                          </div>
                          <div>
                            <span className="text-gray-400">Room</span>
                            <div className="capitalize text-gray-700">{p.room_type || "—"} {p.room_number ? `(${p.room_number})` : ""}</div>
                          </div>
                          <div>
                            <span className="text-gray-400">Visa Status</span>
                            <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full font-semibold capitalize ${VISA_STATUS_COLORS[p.visa_status] || "bg-gray-100 text-gray-600"}`}>
                              {p.visa_status || "—"}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-400">Documents</span>
                            <div className={`flex items-center gap-1 mt-0.5 ${p.document_status === "complete" ? "text-green-700" : "text-orange-600"}`}>
                              {p.document_status === "complete" ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                              <span className="capitalize">{p.document_status || "—"}</span>
                            </div>
                          </div>
                          {p.visa_reference && (
                            <div>
                              <span className="text-gray-400">Visa Ref</span>
                              <div className="font-mono text-gray-700">{p.visa_reference}</div>
                            </div>
                          )}
                        </div>
                        {p.notes && <div className="mt-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">{p.notes}</div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── SERVICES TAB ── */}
        {tab === "services" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Service Components ({services.length})</h2>
              {!showAddService && (
                <button
                  onClick={() => { setShowAddService(true); setEditService(null); }}
                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700"
                >
                  <Plus size={14} /> Add Service
                </button>
              )}
            </div>

            {showAddService && (
              <ServiceForm
                onSave={addService}
                onCancel={() => setShowAddService(false)}
                saving={actionLoading === "add-service"}
              />
            )}

            {services.length === 0 && !showAddService ? (
              <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-sm">
                No service components yet.
              </div>
            ) : (
              <div className="space-y-3">
                {services.map(svc => (
                  <div key={svc.id}>
                    {editService === svc.id ? (
                      <ServiceForm
                        initial={svc}
                        onSave={data => updateService(svc.id, data)}
                        onCancel={() => setEditService(null)}
                        saving={actionLoading === `edit-service-${svc.id}`}
                      />
                    ) : (
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <span className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold border capitalize ${SERVICE_COLORS[svc.service_type] || "bg-gray-50 text-gray-700 border-gray-200"}`}>
                              {SERVICE_ICONS[svc.service_type]}{svc.service_type}
                            </span>
                            <div>
                              <div className="font-medium text-gray-900 text-sm">{svc.description}</div>
                              {svc.notes && <div className="text-xs text-gray-400 mt-0.5">{svc.notes}</div>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            {svc.cost != null && (
                              <span className="text-sm font-semibold text-gray-800">
                                {svc.currency} {svc.cost.toLocaleString("en-GB", { minimumFractionDigits: 0 })}
                              </span>
                            )}
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${
                              svc.status === "confirmed" ? "bg-green-100 text-green-700"
                              : svc.status === "cancelled" ? "bg-red-100 text-red-700"
                              : "bg-yellow-100 text-yellow-700"
                            }`}>{svc.status}</span>
                            <button onClick={() => { setEditService(svc.id); setShowAddService(false); }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md">
                              <Edit2 size={13} />
                            </button>
                            <button disabled={!!actionLoading} onClick={() => deleteService(svc.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md disabled:opacity-40">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── FINANCIALS TAB ── */}
        {tab === "financials" && (
          <div className="max-w-lg space-y-5">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Agreed Commercial Terms</h2>
              <p className="text-xs text-gray-400 italic">These values were locked at the time of booking and cannot be changed.</p>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Occupancy Type</dt>
                  <dd className="text-sm font-medium text-gray-800 capitalize">{booking.selected_occupancy || "—"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Travellers</dt>
                  <dd className="text-sm font-medium text-gray-800">{booking.number_of_travellers}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Price per Person</dt>
                  <dd className="text-sm font-semibold text-gray-800">
                    {booking.agreed_currency} {(booking.agreed_price_per_person || 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}
                  </dd>
                </div>
                <div className="flex justify-between border-t border-gray-100 pt-3">
                  <dt className="text-base font-bold text-gray-800">Agreed Total</dt>
                  <dd className="text-xl font-bold text-amber-800">
                    {booking.agreed_currency} {(booking.agreed_total_price || 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}
                  </dd>
                </div>
              </dl>
            </div>

            {services.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-3">
                <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Service Costs</h2>
                {services.filter(s => s.cost != null).map(svc => (
                  <div key={svc.id} className="flex justify-between text-sm">
                    <span className="text-gray-600 capitalize">{svc.service_type} — {svc.description}</span>
                    <span className="font-medium text-gray-800">{svc.currency} {svc.cost.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</span>
                  </div>
                ))}
                {totalServiceCost > 0 && (
                  <div className="flex justify-between border-t border-gray-100 pt-2 font-semibold text-sm">
                    <span className="text-gray-700">Total Service Costs</span>
                    <span className="text-gray-900">EUR {totalServiceCost.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── DOCUMENTS TAB ── */}
        {tab === "documents" && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-10 text-center text-gray-400 text-sm">
            <FileText size={32} className="mx-auto mb-3 text-gray-300" />
            <p>Document management coming soon.</p>
            <p className="text-xs mt-1">Use the Invoice system to generate invoices for this booking using source_type="umrah_booking".</p>
          </div>
        )}

        {/* ── ACTIVITY TAB ── */}
        {tab === "activity" && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Activity</h2>
            <dl className="space-y-3">
              {[
                ["Created", `${fmtDateTime(booking.created_at)}${booking.created_by ? ` by ${booking.created_by}` : ""}`],
                ["Last Updated", `${fmtDateTime(booking.updated_at)}${booking.updated_by ? ` by ${booking.updated_by}` : ""}`],
                ["Source Quotation", booking.quotation_number || "—"],
                ["Package Reference", booking.package_reference || "—"],
              ].map(([label, val]) => (
                <div key={label} className="flex gap-4">
                  <dt className="text-xs text-gray-400 w-32 shrink-0">{label}</dt>
                  <dd className="text-sm text-gray-700">{val}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
